import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { BiometryType, NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Device } from '@capacitor/device';
import { styled } from 'styled-components';
import Helmet from '../../../commonComponents/Helmet';
import { loginWithBiometric } from '../../../core/apiWithDomainHandler';
import {
	clearRedirectUrlLocalStorage,
	clearRegistrationDataLocalStorage,
	getRedirectUrl,
	getUserEmailFromLocalStorage,
	saveTokens,
	setUserEmailToLocalStorage
} from '../../../core/localStorage';
import { BiometricLoginModel } from '../../../models/AccountModels';
import { dashboardPageUrl, biometricLoginSetupPageUrl } from '../../../core/constants';
import TwoFactorAuthenticationResult, { AuthenticationResult } from '../../../models/TwoFactorAuthenticationResult';
import useAsyncAction from '../../../commonComponents/Hooks';
import faceIdFailedSvgV2 from '../../../images/faceIdFailedV2.svg';
import { defaultModalMessage, getBiometricTypeText } from '../../../core/domain';
import { ApiResultMessages, LoginFlowSubPages, MessageTypes } from '../../../core/enums';
import { isWebApp } from '../../../lib/utils';
import { verifyBiometric } from '../biometricLogin/BiometricLoginSetupPage';
import LoginForm from './forms/LoginForm';
import AutoLogoutLayout from '../common/Layout';
import InitialSubLoginPage from './InitialSubLoginPage';
import kellyPartnersLogoBlueSvg from '../../../images/kellyPartnersLogoBlue.svg';
import { tabletPortraitMinWidth } from '../../cssConstants';
import MobileNumberConfirmationForm, { sendSmsCodeToMobile } from './forms/MobileNumberConfirmationForm';
import SmsVerificationForm from './forms/SmsVerificationForm';
import BottomModal from '../../../commonComponents/bottomModal/BottomModal';
import { ApiResultMessageModel, getAndValidateApiMessage } from '../../../models/ApiResultMessage';
import SubPageLayout from '../common/SubPageLayout';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import useLoginStatusStore from '../../../zustandStore/loginStatusStore';
import { hasValue } from '../../../lib/lib';
import { useMessageStore } from '../../../zustandStore/messageStore';

const LoginPage = () => {
	const setLoggedIn = useLoginStatusStore((state) => state.setLoggedIn);
	const navigate = useNavigateWithAppName();
	const location = useLocation();
	const { state } = location;
	const from = getRedirectUrl() ?? (hasValue(state?.from) ? `${state?.from?.pathname}${state?.from?.search}` : null);
	const [searchParams] = useSearchParams();
	const loggedOut = searchParams.get('sessionExpired') === 'true';
	const isAccountDeleted = searchParams.get('isAccountDeleted') === 'true';
	const isLoggedOutOrNewUser = loggedOut || isAccountDeleted || !hasValue(getUserEmailFromLocalStorage());

	const updateMessage = useMessageStore((s) => s.updateMessage);
	const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
	const [biometricType, setBiometricType] = useState<BiometryType>();
	const [biometricTypeText, setBiometricTypeText] = useState<string>();
	const [deviceUniqueId, setDeviceUniqueId] = useState<string>();
	const [authenticationResult, setAuthenticationResult] = React.useState<AuthenticationResult>();
	const [userEmail, setUserEmail] = React.useState<string>();
	const [userPassword, setUserPassword] = React.useState<string>();
	const [isMobileConfirmed, setIsMobileConfirmed] = useState(false);

	const [modalMessage, setModalMessage] = useState<ApiResultMessageModel>({
		title: '',
		description: []
	});

	// Keep the sequential sub pages in login flow:
	const [showInitialLoginPage, setShowInitialLoginPage] = React.useState(isLoggedOutOrNewUser);
	const [showLoginForm, setShowLoginForm] = React.useState(!isLoggedOutOrNewUser);
	const [showMobileNumberConfirmationPage, setShowMobileNumberConfirmationPage] = React.useState(false);
	const [showSmsVerificationPage, setShowSmsVerificationPage] = React.useState(false);
	const [showBiometricLoginFailedPage, setShowBiometricLoginFailedPage] = React.useState(false);

	const performAsyncAction = useAsyncAction();

	// Clear local storage
	clearRegistrationDataLocalStorage();

	useEffect(() => {
		if (isAccountDeleted) {
			const newModalMessage: ApiResultMessageModel = {
				title: 'App account deleted',
				description: ['Your account has been successfully deleted. You can create a new account at any time.'],
				errorType: MessageTypes.Error,
				actions: [
					{
						label: 'Close',
						callback: handleCloseModal
					}
				]
			};
			setModalMessage(newModalMessage);
		}
	}, [isAccountDeleted]);

	useEffect(() => {
		performAsyncAction(async () => {
			if (isWebApp || showInitialLoginPage) return;

			const result = await NativeBiometric.isAvailable();
			setIsBiometricAvailable(result.isAvailable);

			if (!result.isAvailable) return;
			setBiometricType(result.biometryType);
			const newBiometricTypeText = getBiometricTypeText(result.biometryType);
			setBiometricTypeText(newBiometricTypeText);

			try {
				const deviceId = await Device.getId();
				setDeviceUniqueId(deviceId.identifier);
				const credentials = await NativeBiometric.getCredentials({
					server: deviceId.identifier
				});
				// When first time, there is no saved credentials, so above getCredentials will throw error and verifyIdentity won't be run.
				if (!credentials?.password) return;

				setUserEmail(credentials.username);
				setUserPassword(credentials.password);
				await biometricLogin(credentials.username, credentials.password, result.biometryType, deviceId.identifier);
			} catch {
				// Ignore if there is any error from biometric login attempt, so will show normal login form page.
			}
		});
	}, [showInitialLoginPage]);

	const biometricLogin = async (email: string, password: string, type: BiometryType, uniqueId: string) => {
		if (!password) {
			showSubPage(LoginFlowSubPages.BiometricLoginFailedPage);
			return;
		}

		const verified = await verifyBiometric(type);
		if (!verified) {
			showSubPage(LoginFlowSubPages.BiometricLoginFailedPage);
			return;
		}

		const biometricLoginModel = {
			email,
			password,
			uniqueId
		} as BiometricLoginModel;
		const apiResult = await loginWithBiometric(biometricLoginModel);
		if (!apiResult.succeeded) {
			if (apiResult.response === ApiResultMessages.ClientAccountLocked) {
				const apiMessage = getAndValidateApiMessage(apiResult.response as string);
				setModalMessage(apiMessage);
			} else {
				// Failed biometric login from API, should delete cached credentials
				setUserPassword(null);
				await NativeBiometric.deleteCredentials({
					server: uniqueId
				});
				updateMessage(`${apiResult.response}`);
			}
		} else {
			saveTokensAndRedirectToDashboard(apiResult.response as AuthenticationResult);
		}
	};

	const showSubPage = (nextSubPage: LoginFlowSubPages) => {
		setShowInitialLoginPage(nextSubPage === LoginFlowSubPages.InitialLoginPage);
		setShowLoginForm(nextSubPage === LoginFlowSubPages.LoginFormPage);
		setShowMobileNumberConfirmationPage(nextSubPage === LoginFlowSubPages.MobileNumberConfirmationPage);
		setShowSmsVerificationPage(nextSubPage === LoginFlowSubPages.SmsVerificationPage);
		setShowBiometricLoginFailedPage(nextSubPage === LoginFlowSubPages.BiometricLoginFailedPage);
	};

	const handleCloseModal = () => {
		setModalMessage(defaultModalMessage);
	};

	const handleLoginFormSubmit = async (
		email: string,
		password: string,
		apiMessage?: ApiResultMessageModel,
		authResult?: AuthenticationResult
	) => {
		setUserEmail(email);
		setUserPassword(password);

		if (apiMessage) {
			setModalMessage(apiMessage);
			return;
		}

		setAuthenticationResult(authResult);

		const mobileNumber = authResult?.mobileNumber;
		const securityToken = authResult?.securityToken;
		setIsMobileConfirmed(hasValue(mobileNumber));

		// For Apple testing account to skip sms verification
		if (hasValue(authResult.accessToken) && hasValue(authResult.refreshToken)) {
			saveTokensAndRedirectToDashboard(authResult);
		}

		if (hasValue(mobileNumber)) {
			const { signal } = new AbortController();
			await sendSmsCodeToMobile(email, mobileNumber, securityToken, navigate, signal);
			showSubPage(LoginFlowSubPages.SmsVerificationPage);
			return;
		}

		showSubPage(LoginFlowSubPages.MobileNumberConfirmationPage);
	};

	const handleConfirmMobileNumber = (mobile: string) => {
		setAuthenticationResult((prevState) => ({ ...prevState, mobileNumber: mobile }));
		showSubPage(LoginFlowSubPages.SmsVerificationPage);
	};

	const handle2FaSuccess = (result: TwoFactorAuthenticationResult) => {
		saveTokensAndPrepareForDashboardPage(result);
		if (result.skipBoimetricLoginSetup || !isBiometricAvailable) redirectToFromPageOrGoToDashboardPage();
		else goToBiometricSetupPage();
	};

	const saveTokensAndRedirectToDashboard = (result: AuthenticationResult) => {
		saveTokensAndPrepareForDashboardPage(result);
		redirectToFromPageOrGoToDashboardPage();
	};

	const saveTokensAndPrepareForDashboardPage = (result: AuthenticationResult) => {
		setLoggedIn(true);
		saveTokens(result.refreshToken, result.accessToken);
		setUserEmailToLocalStorage(userEmail);
	};

	const redirectToFromPageOrGoToDashboardPage = () =>
		setTimeout(() => {
			navigate(from ?? dashboardPageUrl, { replace: true });
			clearRedirectUrlLocalStorage();
		}, 0);

	const goToBiometricSetupPage = () =>
		setTimeout(
			() =>
				navigate(biometricLoginSetupPageUrl, { state: { email: userEmail, password: userPassword, fromLogin: true } }),
			0
		);

	const handleBiometricLoginRetry = async () => {
		await performAsyncAction(async () => {
			await biometricLogin(userEmail, userPassword, biometricType, deviceUniqueId);
		});
	};

	const handleLoginButtonClick = async () => {
		showSubPage(LoginFlowSubPages.LoginFormPage);
	};

	const handle2FaSubpageBackButtonClick = (isSmsVerificationPage: boolean) => () => {
		if (isSmsVerificationPage && !isMobileConfirmed) {
			showSubPage(LoginFlowSubPages.MobileNumberConfirmationPage);
		} else showSubPage(LoginFlowSubPages.LoginFormPage);
	};

	const handleBackToInitialLoginSubPage = () => {
		showSubPage(LoginFlowSubPages.InitialLoginPage);
	};

	return (
		<>
			{showInitialLoginPage && <InitialSubLoginPage onLoginButtonClick={handleLoginButtonClick} />}
			{(showLoginForm || showBiometricLoginFailedPage) && (
				<AutoLogoutLayout onBackButtonClick={showLoginForm ? handleBackToInitialLoginSubPage : null}>
					<Helmet>
						<title>{showBiometricLoginFailedPage ? `${biometricTypeText} Login Failed` : 'Login Form'}</title>
					</Helmet>
					<HeaderWrapper>
						{!showBiometricLoginFailedPage && <HeaderLogo src={kellyPartnersLogoBlueSvg} alt="Kelly+Partners Logo" />}
					</HeaderWrapper>
					{showLoginForm && <LoginForm onSubmit={handleLoginFormSubmit} />}
					{showBiometricLoginFailedPage && (
						<SubPageLayout
							icon={faceIdFailedSvgV2}
							iconAlt="biometric icon"
							title={`${biometricTypeText} login failed`}
							paragraphs={['Face ID failed. Try again or log in with your email address and password.']}
							primaryButtonText="Try again"
							onPrimaryButtonClick={handleBiometricLoginRetry}
							secondaryButtonText="Return to login"
							onSecondaryButtonClick={handleLoginButtonClick}
						/>
					)}
				</AutoLogoutLayout>
			)}
			{(showMobileNumberConfirmationPage || showSmsVerificationPage) && (
				<AutoLogoutLayout
					headerText={showSmsVerificationPage ? 'Two-factor authentication' : 'Set up two-factor authentication'}
					onBackButtonClick={handle2FaSubpageBackButtonClick(showSmsVerificationPage)}
				>
					<Helmet>
						<title>Two-factor Authentication</title>
					</Helmet>
					{showMobileNumberConfirmationPage && (
						<MobileNumberConfirmationForm
							email={userEmail}
							securityToken={authenticationResult.securityToken}
							onSubmit={handleConfirmMobileNumber}
						/>
					)}
					{showSmsVerificationPage && (
						<SmsVerificationForm
							email={userEmail}
							securityToken={authenticationResult.securityToken}
							mobileNumber={authenticationResult.mobileNumber}
							onVerifySuccess={handle2FaSuccess}
							isFromLogin
						/>
					)}
				</AutoLogoutLayout>
			)}
			<BottomModal
				title={modalMessage.title}
				body={modalMessage.description}
				type={modalMessage.errorType}
				actions={modalMessage.actions}
				open={hasValue(modalMessage.title)}
				onClose={handleCloseModal}
			/>
		</>
	);
};

export default LoginPage;

const HeaderWrapper = styled.section`
	text-align: center;
	padding-top: 40px;
`;

const HeaderLogo = styled.img`
	height: auto;
	width: 227px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		width: 350px;
	}
`;
